/* istanbul ignore file */
import React from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { toast } from 'react-toastify';
import SahamDrawer from 'components/SahamDrawer';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { translate, arrayTranslate } from 'containers/services/translate';
import messages from 'containers/PreOpeningPage/messages';
import styled from 'styled-components';
import {
  TextField,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  FormHelperText,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@material-ui/core';
import { CheckCircle } from '@material-ui/icons';
import DateFnsUtils from '@date-io/date-fns';
import { PARTIES_CLAIM_STATUS, VICTIM } from 'containers/data-structure';
import { isEmpty, get, isNull, uniqueId, isNil } from 'lodash';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { Listings, SagisConfig } from 'containers/AppContainer/typings';
import moment from 'moment-timezone';
import frLocale from 'date-fns/locale/fr';
import {
  INJURY_POST,
  CHOICES,
  GENDER,
  MINOR_VALUE,
} from 'containers/PreOpeningPage/constants';
import validate from 'containers/services/validateForm';
import { displayErrorText } from 'containers/services/validateForm';
import { formsAreValidated } from 'containers/services/validateForm';
import {
  getMoroccanCities,
  getCity,
  formatVictimPayloadToUpdate,
} from 'containers/RecapManagerPage/utils';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import recapManagerMessages from 'containers/RecapManagerPage/messages';
import { rcCoporeal } from 'containers/CourtManagerLegalProceedings/constants';
import { filterVisibleOnly } from 'containers/services/filters';
import { resolveSagisClaim } from 'helpers/utils';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';
import { displayErrorInputCin } from 'containers/RecapManagerPage/utils';
import InputMask from 'react-input-mask';
import { Autocomplete as AutoComplete } from '@material-ui/lab';
import { isOtherProfession } from 'containers/CourtManagerLegalProceedings/utils';
import { OTHER_PROFESSION } from 'containers/RecapManagerPage/constants';
import { VICTIM_ITT_AVAILABILITY } from 'containers/PreOpeningPage/constants';
import {
  checkClassVictim,
  checkVictimAge,
} from 'containers/PreOpeningPage/utils';
import { PROFESSION_CODES } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/constants';
import { useSelector } from 'react-redux';
import {
  removeRejectedVictims,
  removeDeletedItems,
  displayVehiclePassenger,
} from 'services/utils';

export const victimConstraints = (victim: any) => {
  return {
    [VICTIM.CIN]: {
      presence: true,
      format: {
        pattern: /[A-Za-z]{1,2}\d{1,6}[A-Za-z]?|\d{1,7}[A-Za-z]/,
        message: messages.cinInvalid,
      },
    },
    [VICTIM.ADDRESS]: {
      presence: true,
    },
    [VICTIM.CITY]: {
      presence: true,
    },
    [VICTIM.CLASS_KEY]: {
      presence: true,
    },
    [VICTIM.DATE_OF_BIRTH]: {
      presence: true,
    },
    [VICTIM.FIRST_NAME]: {
      presence: true,
    },
    [VICTIM.INJURY_POST_KEY]: {
      presence: true,
    },
    [VICTIM.LAST_NAME]: {
      presence: true,
    },
    [VICTIM.OPPOSITION]: {
      presence: true,
    },
    [VICTIM.PROCEDURE_ID]: {
      presence: true,
    },
    [VICTIM.PROFESSION]: {
      presence:
        String(get(victim, [VICTIM.PROFESSION_TYPE])) === OTHER_PROFESSION,
    },
    [VICTIM.PROFESSION_TYPE]: {
      presence: true,
    },
    [VICTIM.QUALITY_KEY]: {
      presence: true,
    },
    [VICTIM.TELEPHONE_NUMBER]: {
      presence: true,
      format: {
        pattern: /^\d{10}$/,
        message: messages.phoneNumberInvalid,
      },
    },
    [VICTIM.ITT]: {
      presence:
        !INJURY_POST.DECEASED.includes(
          String(get(victim, [VICTIM.INJURY_POST_KEY])),
        ) &&
        String(get(victim, [VICTIM.ITT_AVAILABILITY])) ===
          VICTIM_ITT_AVAILABILITY.AVAILABLE,
    },
    [VICTIM.WORK_ACCIDENT_ID]: {
      presence: true,
    },
    [VICTIM.IS_EXCLUDED]: {
      presence: featureService.hasFeature(
        FEATURES.IS_VICTIM_EXCLUDED_FROM_COVERAGE,
      ),
    },
    [VICTIM.GENDER]: {
      presence: INJURY_POST.DECEASED.includes(
        String(get(victim, [VICTIM.INJURY_POST_KEY])),
      ),
    },
  };
};

export const victimUsageConstraints = (victim: any) => {
  return {
    [VICTIM.FIRST_NAME]: {
      presence: true,
    },
    [VICTIM.INJURY_POST_KEY]: {
      presence: true,
    },
    [VICTIM.LAST_NAME]: {
      presence: true,
    },
  };
};
export const victimValidator = (victim: any) => {
  return validate(victim, victimConstraints(victim));
};
export const victimUsageValidator = (victim: any) => {
  return validate(victim, victimUsageConstraints(victim));
};

const Container = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  .text {
    font-size: 13px;
    color: #374065;
    letter-spacing: 0px;
    font-weight: medium;
    font-family: Roboto;
  }
  .input {
    margin-bottom: 20px;
    font-size: 13px;
  }
  .error {
    margin-top: -5px;
    color: red;
    text-align: center;
  }
  .errorSelect {
    margin-top: 9px;
    color: red;
  }
`;
const StyledSelect = styled(Select)`
  &.MuiInput-underline.Mui-disabled:before {
    border-bottom-style: solid;
  }
  .MuiSelect-icon {
    color: black;
  }
  font-weight: 500;
`;

export interface VictimDrawerProps {
  intl: IntlShape;
  open: boolean;
  listings: Listings;
  handleToggleDrawer: Function;
  index: number;
  data: any;
  setClaimRecap?: Function;
  closeVictimDrawer: Function;
  isCourtMonitoringManagerUpdate?: boolean;
  requestUpdateVictimInfo?: Function;
  policyInfo?: { sagis: {} };
  setDataClaimToUpdate?: Function;
  dataClaimToUpdate?: any;
  inputLabelProps?: boolean;
  isCourtManager?: boolean;
}

const VictimDrawer: React.FC<VictimDrawerProps> = (props): JSX.Element => {
  const inputLabel = React.useRef<HTMLLabelElement>(null);

  let [data, setData] = React.useState<any>({});
  const [victimValidation, setVictimValidation] = React.useState({});

  const priorityReasonClaimId = useSelector((state: any) =>
    get(state.toJS(), ['recap', 'claimRecap', 'claim', 'priorityReasonId']),
  );

  React.useEffect(() => {
    setData({
      ...props.data,
      victims: removeDeletedItems(get(props.data, 'victims', [])),
      deletedVictims: [
        ...get(props.data, 'deletedVictims', []),
        ...get(props.data, 'victims', []).filter((el: any) => el.isDeleted),
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readOnlyInput =
    props.isCourtManager && props.isCourtMonitoringManagerUpdate;
  const handleChange = (field: string) => (
    event: React.ChangeEvent<{
      name?: string;
      value?: any;
    }>,
    valueField?: any,
  ) => {
    const { value } = event.target;
    const index = props.index;
    const { victims } = data;

    switch (field) {
      case VICTIM.CIN:
        victims[index][VICTIM.CIN] = value;
        break;
      case VICTIM.LAST_NAME:
        victims[index][VICTIM.LAST_NAME] = value;
        break;
      case VICTIM.FIRST_NAME:
        victims[index][VICTIM.FIRST_NAME] = value;
        break;
      case VICTIM.TELEPHONE_NUMBER:
        victims[index][VICTIM.TELEPHONE_NUMBER] = value.replace(/\s+/g, '');
        break;
      case VICTIM.PROCEDURE_ID:
        victims[index][VICTIM.PROCEDURE_ID] = value;
        break;
      case VICTIM.DAMAGE_TYPE:
        victims[index][VICTIM.DAMAGE_TYPE] = value;
        break;
      case VICTIM.PROFESSION:
        victims[index][VICTIM.PROFESSION] = value;
        break;
      case VICTIM.PROFESSION_TYPE:
        victims[index][VICTIM.PROFESSION_TYPE] = get(valueField, 'ItemKey');
        victims[index][VICTIM.PROFESSION] =
          get(valueField, 'ItemKey') === OTHER_PROFESSION
            ? victims[index][VICTIM.PROFESSION]
            : null;
        break;
      case VICTIM.WORK_ACCIDENT_ID:
        victims[index][VICTIM.WORK_ACCIDENT_ID] = value;
        break;
      case VICTIM.CLASS_KEY:
        victims[index][VICTIM.CLASS_KEY] = value;
        if (value === MINOR_VALUE) {
          victims[index][VICTIM.NUMBER_OF_SPOUSE] = 0;
          victims[index][VICTIM.NUMBER_OF_DEPENDENTS] = 0;
          victims[index][VICTIM.NUMBER_OF_CHILDREN] = 0;
        }
        break;
      case VICTIM.ITT_AVAILABILITY:
        victims[index][VICTIM.ITT_AVAILABILITY] = value;
        victims[index][VICTIM.ITT] =
          String(value) === VICTIM_ITT_AVAILABILITY.NOT_AVAILABLE
            ? ''
            : victims[index][VICTIM.ITT];
        break;
      case VICTIM.ITT:
        victims[index][VICTIM.ITT] =
          typeof value === 'string' ? value.replace(/[^0-9]/g, '') : value;
        break;
      case VICTIM.CITY:
        victims[index][VICTIM.CITY] = get(valueField, 'ItemKey');
        break;
      case VICTIM.ADDRESS:
        victims[index][VICTIM.ADDRESS] = value;
        break;
      case VICTIM.IS_EXCLUDED:
        victims[index][VICTIM.IS_EXCLUDED] = value === 'true' ? true : false;
        break;
      case VICTIM.INJURY_POST_KEY:
        victims[index][VICTIM.INJURY_POST_KEY] = value;
        break;
      case VICTIM.QUALITY_KEY:
        victims[index][VICTIM.QUALITY_KEY] = value;
        break;
      case VICTIM.VEHICLE_PASSENGER_ID:
        victims[index][VICTIM.VEHICLE_PASSENGER_ID] = value;
        break;
      case VICTIM.OPPOSITION:
        victims[index][VICTIM.OPPOSITION] = !!(value === 1);
        break;
      case VICTIM.GENDER:
        victims[index][VICTIM.GENDER] = value;
        break;
      case VICTIM.NUMBER_OF_SPOUSE:
        victims[index][VICTIM.NUMBER_OF_SPOUSE] = Number(value);
        break;
      case VICTIM.NUMBER_OF_DEPENDENTS:
        victims[index][VICTIM.NUMBER_OF_DEPENDENTS] = Number(value);
        break;
      case VICTIM.NUMBER_OF_CHILDREN:
        victims[index][VICTIM.NUMBER_OF_CHILDREN] = Number(value);
        break;
      case VICTIM.ANNUAL_INCOME:
        victims[index][VICTIM.ANNUAL_INCOME] =
          typeof value === 'string' ? value.replace(',', '.') : value;
        break;
      default:
        return victims;
    }

    setData({ ...data, victims });
  };

  const handleDateChange = (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    const { victims } = data;
    victims[props.index][VICTIM.DATE_OF_BIRTH] = moment(
      theDate as Date,
    ).format();

    setData({ ...data, victims });

    return;
  };

  const victim = get(data, `victims[${props.index}]`);
  const successCallBackUpdateVictim = () => {
    toast.success(translate(messages.successUpdateVictim, props));
  };

  const decisionVictim =
    !isNull(get(victim, 'decision')) &&
    // eslint-disable-next-line eqeqeq
    get(victim, 'decision.status') != PARTIES_CLAIM_STATUS.VALIDATED;

  const handleValidation = () => {
    const { victims } = data;
    const updatePriorityVictim =
      ![...removeRejectedVictims(victims)].find((v: any) =>
        PROFESSION_CODES.includes(v.professionCode),
      ) && priorityReasonClaimId === 5;
    if (
      props.isCourtMonitoringManagerUpdate &&
      props.requestUpdateVictimInfo &&
      !props.isCourtManager
    ) {
      const claim = get(
        resolveSagisClaim(props.policyInfo),
        'offeredRisks',
        [],
      ).find(
        (element: { riskName: string }) => element.riskName === rcCoporeal,
      );
      const payload = formatVictimPayloadToUpdate(
        victim,
        get(data, 'claim.claimNumber'),
        claim?.riskId,
        decisionVictim,
        get(victim, 'decision'),
        props.listings,
        updatePriorityVictim,
        priorityReasonClaimId,
      );
      const validator = victimValidator(victim) || {};
      setVictimValidation(validator);
      if (formsAreValidated(validator)) {
        props.requestUpdateVictimInfo(payload, successCallBackUpdateVictim);
        props.setClaimRecap && props.setClaimRecap(data);
        props.closeVictimDrawer();
      }
    } else if (props.isCourtManager && props.requestUpdateVictimInfo) {
      const claim = get(
        resolveSagisClaim(props.policyInfo),
        'offeredRisks',
        [],
      ).find(
        (element: { riskName: string }) => element.riskName === rcCoporeal,
      );
      const payload = formatVictimPayloadToUpdate(
        victim,
        get(data, 'claim.claimNumber'),
        claim?.riskId,
        false,
        {},
        props.listings,
        updatePriorityVictim,
        priorityReasonClaimId,
      );
      props.requestUpdateVictimInfo(payload, successCallBackUpdateVictim);
      props.setClaimRecap && props.setClaimRecap(data);
      props.closeVictimDrawer();
    } else {
      const validator = victimValidator(victim) || {};
      setVictimValidation(validator);
      if (formsAreValidated(validator)) {
        victim['isModified'] = true;
        props.setClaimRecap && props.setClaimRecap(data);
        props.setDataClaimToUpdate &&
          props.setDataClaimToUpdate({
            ...props.dataClaimToUpdate,
            victims: get(data, 'victims', []),
          });
        props.closeVictimDrawer();
      }
    }
  };

  const translateArrayErrorMessages = arrayTranslate(props.intl.formatMessage);
  const { message } = displayErrorText(
    victimValidation,
    translateArrayErrorMessages(victimValidation, true),
  );

  const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: (option: SagisConfig) => option?.ItemDescription,
  });

  return (
    <SahamDrawer
      open={props.open}
      handleToggleDrawer={() => props.handleToggleDrawer(null, true)}
      title={translate({ ...messages.victimCardTitle }, props)}
      handleValidateDrawer={handleValidation}
      confirmButtonTitle={translate(
        recapManagerMessages.drawerSaveFormButtonTitle,
        props,
      )}
      isSaveButtonDisabled={
        displayErrorInputCin(data, victim, props.dataClaimToUpdate) ||
        (!props.isCourtMonitoringManagerUpdate &&
          dateStringToLocalDate(get(victim, [VICTIM.DATE_OF_BIRTH], '')) ===
            'Invalid date') ||
        (!props.isCourtMonitoringManagerUpdate &&
          checkClassVictim(
            get(victim, [VICTIM.DATE_OF_BIRTH]),
            get(props.data, 'claim.lossDate'),
            get(victim, [VICTIM.CLASS_KEY]),
          ))
      }
    >
      <Container>
        <TextField
          label={translate({ ...messages.victimCin }, props)}
          className="input"
          name={VICTIM.CIN}
          value={get(victim, [VICTIM.CIN])}
          onChange={handleChange(VICTIM.CIN)}
          InputProps={{ inputProps: { maxlength: 20 } }}
          disabled={readOnlyInput}
          error={
            !!get(victimValidation, [VICTIM.CIN]) ||
            displayErrorInputCin(data, victim, props.dataClaimToUpdate)
          }
          helperText={
            displayErrorInputCin(data, victim, props.dataClaimToUpdate)
              ? translate(messages.errorCinVictim, props)
              : message[VICTIM.CIN]
          }
        ></TextField>
        <TextField
          label={translate({ ...messages.victimLastName }, props)}
          className="input"
          name={VICTIM.LAST_NAME}
          disabled={readOnlyInput}
          value={get(victim, [VICTIM.LAST_NAME])}
          onChange={handleChange(VICTIM.LAST_NAME)}
          error={!!get(victimValidation, [VICTIM.LAST_NAME])}
          helperText={message[VICTIM.LAST_NAME]}
        ></TextField>
        <TextField
          label={translate({ ...messages.victimFirstName }, props)}
          className="input"
          name={VICTIM.FIRST_NAME}
          disabled={readOnlyInput}
          value={get(victim, [VICTIM.FIRST_NAME])}
          onChange={handleChange(VICTIM.FIRST_NAME)}
          error={!!get(victimValidation, [VICTIM.FIRST_NAME])}
          helperText={message[VICTIM.FIRST_NAME]}
        ></TextField>
        {(!props.isCourtMonitoringManagerUpdate || decisionVictim) && (
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
            <KeyboardDatePicker
              autoOk
              variant="inline"
              invalidDateMessage={
                !!get(victimValidation, [VICTIM.DATE_OF_BIRTH])
                  ? message[VICTIM.DATE_OF_BIRTH]
                  : ''
              }
              format="dd/MM/yyyy"
              id="date-picker-inline-victim-date-of-birth"
              label={translate({ ...messages.victimDateOfBirth }, props)}
              className="input"
              disabled={readOnlyInput}
              name={VICTIM.DATE_OF_BIRTH}
              value={dateStringToLocalDate(
                get(victim, [VICTIM.DATE_OF_BIRTH], ''),
              )}
              onChange={handleDateChange}
              error={
                dateStringToLocalDate(
                  get(victim, [VICTIM.DATE_OF_BIRTH], ''),
                ) === 'Invalid date' ||
                checkVictimAge(
                  get(victim, [VICTIM.DATE_OF_BIRTH]),
                  get(props.data, 'claim.lossDate'),
                )
              }
              helperText={
                dateStringToLocalDate(
                  get(victim, [VICTIM.DATE_OF_BIRTH], ''),
                ) === 'Invalid date'
                  ? translate(messages.invalidDate, props)
                  : checkVictimAge(
                      get(victim, [VICTIM.DATE_OF_BIRTH]),
                      get(props.data, 'claim.lossDate'),
                    )
                  ? translate(messages.birthDateValidation, props)
                  : translate(messages.invalidDate, props)
              }
            />
          </MuiPickersUtilsProvider>
        )}
        {(!props.isCourtMonitoringManagerUpdate || decisionVictim) && (
          <>
            <FormControl
              className="input"
              error={!!get(victimValidation, [VICTIM.GENDER])}
            >
              <InputLabel
                ref={inputLabel}
                id="simple-select-outlined-label-work-accident"
              >
                {translate({ ...messages.sexe }, props)}
              </InputLabel>
              <Select
                name={VICTIM.GENDER}
                value={get(victim, [VICTIM.GENDER], '')}
                onChange={handleChange(VICTIM.GENDER)}
                labelWidth={230}
                disabled={readOnlyInput}
              >
                <MenuItem value={GENDER.MALE} key={0}>
                  {GENDER.MALE}
                </MenuItem>
                <MenuItem value={GENDER.FEMALE} key={1}>
                  {GENDER.FEMALE}
                </MenuItem>
              </Select>
            </FormControl>
            {!!get(props.victimValidation, [VICTIM.GENDER]) && (
              <FormHelperText className="errorSelect">
                {message[VICTIM.GENDER]}
              </FormHelperText>
            )}
          </>
        )}
        <InputMask
          mask="9999 999 999"
          value={get(victim, [VICTIM.TELEPHONE_NUMBER])}
          onChange={handleChange(VICTIM.TELEPHONE_NUMBER)}
          disabled={readOnlyInput}
        >
          {() => (
            <TextField
              label={translate({ ...messages.victimTelephone }, props)}
              className="input"
              error={!!get(victimValidation, [VICTIM.TELEPHONE_NUMBER])}
              helperText={message[VICTIM.TELEPHONE_NUMBER]}
            ></TextField>
          )}
        </InputMask>
        {(!props.isCourtMonitoringManagerUpdate || decisionVictim) && (
          <FormControl
            className="input"
            error={!!get(victimValidation, [VICTIM.QUALITY_KEY])}
          >
            <InputLabel
              ref={inputLabel}
              id="simple-select-outlined-label-procedure"
            >
              {translate({ ...messages.victimQualityKey }, props)}
            </InputLabel>
            <Select
              name={VICTIM.QUALITY_KEY}
              value={get(victim, [VICTIM.QUALITY_KEY], '')}
              onChange={handleChange(VICTIM.QUALITY_KEY)}
              disabled={readOnlyInput}
            >
              {!isEmpty(get(props.listings, 'qualityOfVictim', [])) &&
                props.listings!.qualityOfVictim.map(classItem => (
                  <MenuItem value={classItem.ItemKey} key={classItem.ItemKey}>
                    {classItem.ItemDescription}
                  </MenuItem>
                ))}
            </Select>
            {!!get(victimValidation, [VICTIM.QUALITY_KEY]) && (
              <FormHelperText className="errorSelect">
                {message[VICTIM.QUALITY_KEY]}
              </FormHelperText>
            )}
          </FormControl>
        )}
        {(!props.isCourtMonitoringManagerUpdate || decisionVictim) &&
          displayVehiclePassenger(victim) && (
            <FormControl
              className="input"
              error={!!get(victimValidation, [VICTIM.VEHICLE_PASSENGER_ID])}
            >
              <InputLabel
                ref={inputLabel}
                id="simple-select-outlined-label-procedure"
              >
                {translate({ ...messages.vehiclePassenger }, props)}
              </InputLabel>
              <Select
                name={VICTIM.VEHICLE_PASSENGER_ID}
                value={get(victim, [VICTIM.VEHICLE_PASSENGER_ID], '')}
                onChange={handleChange(VICTIM.VEHICLE_PASSENGER_ID)}
                disabled={readOnlyInput}
              >
                {get(props.listings, 'vehiclePassenger', []).map(
                  (classItem: any) => (
                    <MenuItem value={classItem.id} key={classItem.id}>
                      {classItem.title}
                    </MenuItem>
                  ),
                )}
              </Select>
              {!!get(victimValidation, [VICTIM.VEHICLE_PASSENGER_ID]) && (
                <FormHelperText className="errorSelect">
                  {message[VICTIM.VEHICLE_PASSENGER_ID]}
                </FormHelperText>
              )}
            </FormControl>
          )}
        {(!props.isCourtMonitoringManagerUpdate || decisionVictim) && (
          <FormControl
            className="input"
            error={!!get(victimValidation, [VICTIM.INJURY_POST_KEY])}
          >
            <InputLabel
              ref={inputLabel}
              id="simple-select-outlined-label-procedure"
            >
              {translate({ ...messages.victimInjuryPostKey }, props)}
            </InputLabel>
            <Select
              name={VICTIM.INJURY_POST_KEY}
              value={
                get(victim, [VICTIM.INJURY_POST_KEY], '') ===
                INJURY_POST.INJURED[1]
                  ? INJURY_POST.INJURED[0]
                  : get(victim, [VICTIM.INJURY_POST_KEY], '')
              }
              onChange={handleChange(VICTIM.INJURY_POST_KEY)}
              labelWidth={230}
              disabled={readOnlyInput}
            >
              {!isEmpty(get(props.listings, 'prejudice', [])) &&
                props
                  .listings!.prejudice.filter(
                    (el: any) => el.ItemKey !== INJURY_POST.INJURED[1],
                  )
                  .map(e => (
                    <MenuItem value={e.ItemKey} key={e.ItemKey}>
                      {get(e.ItemDescription.split(' '), '[0]', '')}
                    </MenuItem>
                  ))}
            </Select>
            {!!get(victimValidation, [VICTIM.INJURY_POST_KEY]) && (
              <FormHelperText className="errorSelect">
                {message[VICTIM.INJURY_POST_KEY]}
              </FormHelperText>
            )}
          </FormControl>
        )}
        {(!props.isCourtMonitoringManagerUpdate || decisionVictim) && (
          <FormControl
            className="input"
            error={
              !!get(victimValidation, [VICTIM.CLASS_KEY]) ||
              checkClassVictim(
                get(victim, [VICTIM.DATE_OF_BIRTH]),
                get(props.data, 'claim.lossDate'),
                get(victim, [VICTIM.CLASS_KEY]),
              )
            }
          >
            <InputLabel
              ref={inputLabel}
              id="simple-select-outlined-label-class"
            >
              {translate({ ...messages.class }, props)}
            </InputLabel>
            <Select
              name={VICTIM.CLASS_KEY}
              value={get(victim, [VICTIM.CLASS_KEY])}
              onChange={handleChange(VICTIM.CLASS_KEY)}
              labelWidth={140}
              disabled={readOnlyInput}
            >
              {!isEmpty(get(props.listings, 'classVictim', [])) &&
                props.listings!.classVictim.map(classItem => (
                  <MenuItem value={classItem.ItemKey} key={classItem.ItemKey}>
                    {classItem.ItemDescription}
                  </MenuItem>
                ))}
            </Select>
            {(!!get(victimValidation, [VICTIM.CLASS_KEY]) ||
              checkClassVictim(
                get(victim, [VICTIM.DATE_OF_BIRTH]),
                get(props.data, 'claim.lossDate'),
                get(victim, [VICTIM.CLASS_KEY]),
              )) && (
              <FormHelperText className="errorSelect">
                {checkClassVictim(
                  get(victim, [VICTIM.DATE_OF_BIRTH]),
                  get(props.data, 'claim.lossDate'),
                  get(victim, [VICTIM.CLASS_KEY]),
                )
                  ? translate(messages.classKeyValidation, props)
                  : message[VICTIM.CLASS_KEY]}
              </FormHelperText>
            )}
          </FormControl>
        )}
        {(!props.isCourtMonitoringManagerUpdate || decisionVictim) && (
          <FormControl
            className="input"
            error={!!get(victimValidation, [VICTIM.PROCEDURE_ID])}
          >
            <InputLabel
              ref={inputLabel}
              id="simple-select-outlined-label-procedure"
            >
              {translate({ ...messages.typeProcedure }, props)}
            </InputLabel>
            <Select
              name={VICTIM.PROCEDURE_ID}
              value={get(victim, [VICTIM.PROCEDURE_ID], '')}
              onChange={handleChange(VICTIM.PROCEDURE_ID)}
              labelWidth={230}
              disabled={readOnlyInput}
            >
              {!isEmpty(get(props.listings, 'procedures', [])) &&
                filterVisibleOnly(props.listings!.procedures).map(procedure => (
                  <MenuItem value={procedure.id} key={procedure.id}>
                    {procedure.title}
                  </MenuItem>
                ))}
            </Select>
            {!!get(victimValidation, [VICTIM.PROCEDURE_ID]) && (
              <FormHelperText className="errorSelect">
                {message[VICTIM.PROCEDURE_ID]}
              </FormHelperText>
            )}
          </FormControl>
        )}
        <FormControl className="input">
          <AutoComplete
            options={get(props.listings, 'Profession', [])}
            getOptionLabel={(option: any) =>
              `${option?.ItemDescription ? option?.ItemDescription : ''}`
            }
            value={
              (get(props.listings, 'Profession', []).find(
                (item: any) =>
                  item.ItemKey === get(victim, VICTIM.PROFESSION_TYPE),
              ) || {}) as any
            }
            onChange={handleChange(VICTIM.PROFESSION_TYPE)}
            renderInput={(params: any) => (
              <TextField
                {...params}
                variant="standard"
                label={translate(messages.professionType, props)}
                error={!!get(victimValidation, [VICTIM.PROFESSION_TYPE])}
                helperText={message[VICTIM.PROFESSION_TYPE]}
              />
            )}
          />
        </FormControl>
        {isOtherProfession(
          get(props.listings, 'Profession', []),
          get(victim, [VICTIM.PROFESSION_TYPE]),
        ) && (
          <TextField
            label={translate({ ...messages.profession }, props)}
            className="input"
            name={VICTIM.PROFESSION}
            value={get(victim, [VICTIM.PROFESSION])}
            onChange={handleChange(VICTIM.PROFESSION)}
            error={!!get(victimValidation, [VICTIM.PROFESSION])}
            helperText={message[VICTIM.PROFESSION]}
          ></TextField>
        )}
        <FormControl
          className="input"
          error={!!get(victimValidation, [VICTIM.WORK_ACCIDENT_ID])}
        >
          <InputLabel
            ref={inputLabel}
            id="simple-select-outlined-label-work-accident"
          >
            {translate({ ...messages.workAccident }, props)}
          </InputLabel>
          <Select
            name={VICTIM.WORK_ACCIDENT_ID}
            value={get(victim, [VICTIM.WORK_ACCIDENT_ID], '')}
            onChange={handleChange(VICTIM.WORK_ACCIDENT_ID)}
            labelWidth={140}
            disabled={readOnlyInput}
          >
            {!isEmpty(get(props.listings, 'workAccidents', [])) &&
              props.listings!.workAccidents.map(type => (
                <MenuItem value={type.id} key={type.id}>
                  {type.title}
                </MenuItem>
              ))}
          </Select>
          {!!get(victimValidation, [VICTIM.WORK_ACCIDENT_ID]) && (
            <FormHelperText className="errorSelect">
              {message[VICTIM.WORK_ACCIDENT_ID]}
            </FormHelperText>
          )}
        </FormControl>
        {(!props.isCourtMonitoringManagerUpdate || decisionVictim) &&
          get(victim, [VICTIM.INJURY_POST_KEY], '') !== '' &&
          !INJURY_POST.DECEASED.includes(
            get(victim, [VICTIM.INJURY_POST_KEY], ''),
          ) && (
            <>
              <FormControl fullWidth className="input">
                <InputLabel ref={inputLabel}>
                  {translate({ ...messages.availabilityItt }, props)}
                </InputLabel>
                <StyledSelect
                  name={VICTIM.ITT_AVAILABILITY}
                  value={
                    !isNil(get(victim, [VICTIM.ITT], null)) &&
                    get(victim, [VICTIM.ITT]) !== ''
                      ? VICTIM_ITT_AVAILABILITY.AVAILABLE
                      : VICTIM_ITT_AVAILABILITY.NOT_AVAILABLE
                  }
                  onChange={handleChange(VICTIM.ITT_AVAILABILITY)}
                  disabled={readOnlyInput}
                >
                  <MenuItem
                    value={VICTIM_ITT_AVAILABILITY.AVAILABLE}
                    key={uniqueId()}
                  >
                    {translate({ ...messages.available }, props)}
                  </MenuItem>
                  <MenuItem
                    value={VICTIM_ITT_AVAILABILITY.NOT_AVAILABLE}
                    key={uniqueId()}
                  >
                    {translate({ ...messages.notAvailable }, props)}
                  </MenuItem>
                </StyledSelect>

                {!!get(victimValidation, [VICTIM.ITT_AVAILABILITY]) && (
                  <FormHelperText className="errorSelect">
                    {message[VICTIM.ITT_AVAILABILITY]}
                  </FormHelperText>
                )}
              </FormControl>

              {(get(victim, [VICTIM.ITT_AVAILABILITY]) ===
                VICTIM_ITT_AVAILABILITY.AVAILABLE ||
                (!isNil(get(victim, [VICTIM.ITT], null)) &&
                  get(victim, [VICTIM.ITT]) !== '')) && (
                <TextField
                  label={translate({ ...messages.ITT }, props)}
                  className="input"
                  type="number"
                  name={VICTIM.ITT}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                  disabled={readOnlyInput}
                  value={parseInt(get(victim, [VICTIM.ITT]))}
                  onChange={handleChange(VICTIM.ITT)}
                  error={!!get(victimValidation, [VICTIM.ITT])}
                  helperText={message[VICTIM.ITT]}
                ></TextField>
              )}
            </>
          )}
        <FormControl
          className="input"
          error={!!get(victimValidation, [VICTIM.OPPOSITION])}
        >
          <InputLabel
            ref={inputLabel}
            id="simple-select-outlined-label-work-accident"
          >
            {translate({ ...messages.victimOpposition }, props)}
          </InputLabel>
          <Select
            name={VICTIM.OPPOSITION}
            value={
              get(victim, [VICTIM.OPPOSITION], '') === false
                ? 0
                : get(victim, [VICTIM.OPPOSITION], '') === true
                ? 1
                : ''
            }
            disabled={readOnlyInput}
            onChange={handleChange(VICTIM.OPPOSITION)}
            labelWidth={140}
          >
            <MenuItem value={0}>
              {translate({ ...messages.claimDeclarationNo }, props)}
            </MenuItem>
            <MenuItem value={1}>
              {translate({ ...messages.claimDeclarationYes }, props)}
            </MenuItem>
          </Select>
          {!!get(victimValidation, [VICTIM.OPPOSITION]) && (
            <FormHelperText className="errorSelect">
              {message[VICTIM.OPPOSITION]}
            </FormHelperText>
          )}
        </FormControl>
        <Autocomplete
          id="city_complete_1"
          className="input"
          options={getMoroccanCities(get(props.listings, 'villes', []))}
          getOptionLabel={city => city.ItemDescription}
          autoSelect={true}
          onChange={handleChange(VICTIM.CITY)}
          value={getCity(
            get(props.listings, 'villes', []),
            get(victim, [VICTIM.CITY]),
          )}
          disabled={readOnlyInput}
          filterOptions={filterOptions}
          renderInput={params => (
            <TextField
              {...params}
              name={VICTIM.CITY}
              label={translate({ ...messages.victimCity }, props)}
              error={!!get(victimValidation, [VICTIM.CITY])}
              helperText={message[VICTIM.CITY]}
            />
          )}
        />
        <TextField
          label={translate({ ...messages.adress }, props)}
          className="input"
          name={VICTIM.ADDRESS}
          value={get(victim, [VICTIM.ADDRESS])}
          onChange={handleChange(VICTIM.ADDRESS)}
          error={!!get(victimValidation, [VICTIM.ADDRESS])}
          helperText={message[VICTIM.ADDRESS]}
          disabled={readOnlyInput}
        ></TextField>
        {!INJURY_POST.INJURED.includes(
          get(victim, [VICTIM.INJURY_POST_KEY], ''),
        ) && (
          <>
            {(!props.isCourtMonitoringManagerUpdate || decisionVictim) && (
              <>
                <TextField
                  type="number"
                  label={translate({ ...messages.numberOfPartners }, props)}
                  className="input"
                  name={VICTIM.NUMBER_OF_SPOUSE}
                  value={get(victim, [VICTIM.NUMBER_OF_SPOUSE])}
                  onChange={handleChange(VICTIM.NUMBER_OF_SPOUSE)}
                  error={!!get(victimValidation, [VICTIM.NUMBER_OF_SPOUSE])}
                  helperText={message[VICTIM.NUMBER_OF_SPOUSE]}
                  disabled={
                    get(victim, [VICTIM.CLASS_KEY]) === MINOR_VALUE ||
                    readOnlyInput
                  }
                />
                <TextField
                  label={translate({ ...messages.numberOfChildren }, props)}
                  type="number"
                  className="input"
                  name={VICTIM.NUMBER_OF_CHILDREN}
                  value={get(victim, [VICTIM.NUMBER_OF_CHILDREN])}
                  onChange={handleChange(VICTIM.NUMBER_OF_CHILDREN)}
                  error={!!get(victimValidation, [VICTIM.NUMBER_OF_CHILDREN])}
                  helperText={message[VICTIM.NUMBER_OF_CHILDREN]}
                  disabled={
                    get(victim, [VICTIM.CLASS_KEY]) === MINOR_VALUE ||
                    readOnlyInput
                  }
                />
                <TextField
                  type="number"
                  label={translate({ ...messages.numberOfDependents }, props)}
                  className="input"
                  name={VICTIM.NUMBER_OF_DEPENDENTS}
                  value={get(victim, [VICTIM.NUMBER_OF_DEPENDENTS])}
                  onChange={handleChange(VICTIM.NUMBER_OF_DEPENDENTS)}
                  error={!!get(victimValidation, [VICTIM.NUMBER_OF_DEPENDENTS])}
                  helperText={message[VICTIM.NUMBER_OF_DEPENDENTS]}
                  disabled={
                    get(victim, [VICTIM.CLASS_KEY]) === MINOR_VALUE ||
                    readOnlyInput
                  }
                />
                <TextField
                  label={translate({ ...messages.annualOfIncome }, props)}
                  className="input"
                  type="number"
                  name={VICTIM.ANNUAL_INCOME}
                  value={get(victim, [VICTIM.ANNUAL_INCOME])}
                  onChange={handleChange(VICTIM.ANNUAL_INCOME)}
                  error={!!get(victimValidation, [VICTIM.ANNUAL_INCOME])}
                  helperText={message[VICTIM.ANNUAL_INCOME]}
                  disabled={readOnlyInput}
                />
              </>
            )}
          </>
        )}
        <Typography className="text">
          {translate({ ...messages.excludeVictim }, props)}
        </Typography>
        <div>
          <FormControl>
            <RadioGroup
              value={get(victim, [VICTIM.IS_EXCLUDED])}
              onChange={handleChange(VICTIM.IS_EXCLUDED)}
              row
            >
              <FormControlLabel
                control={
                  <Radio
                    color="primary"
                    value={CHOICES.YES}
                    checkedIcon={<CheckCircle />}
                  />
                }
                disabled={readOnlyInput}
                label={translate({ ...messages.yes }, props)}
              />
              <FormControlLabel
                control={
                  <Radio
                    color="primary"
                    value={CHOICES.NO}
                    checkedIcon={<CheckCircle />}
                  />
                }
                disabled={readOnlyInput}
                label={translate({ ...messages.no }, props)}
              />
            </RadioGroup>
            {!!get(victimValidation, [VICTIM.IS_EXCLUDED]) && (
              <FormHelperText className="error">
                {message[VICTIM.IS_EXCLUDED]}
              </FormHelperText>
            )}
          </FormControl>
        </div>
      </Container>
    </SahamDrawer>
  );
};

export default injectIntl(VictimDrawer);
